import {
  Block, EmailControl, FormCol, FormRow, PhoneControl, PrimarySubmitButton, moveWorkflowForwardByName, useScopedSelector, CreateForm, useScope,
  SafeHtml,
} from 'ia-react-core';
import { FormPageTitle, FormSection, InformationsSection, useMarkPageLoaded } from 'omni-package-react-core';
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AxiosResponse } from 'axios';
import { MfaValidationPageState } from '../../../../interfaces/MfaValidationPageState';
import { MfaInformations } from '../../../../interfaces/MfaInformations';
import MfaActionBar from '../../components/MfaActionBar';
import { getIdentityManagementApiRoot } from '../../../../utils/getIdentityManagementApiRoot';
import { MfaChallenge } from '../../../../interfaces/MfaChallenge';

export interface MfaInformationsStepProps { }

const MfaInformationsStep: FC<MfaInformationsStepProps> = () => {
  const { t } = useTranslation('MfaValidationPage');
  const dispatch = useDispatch();
  const { getScopedState } = useScope();

  const informations = useScopedSelector(({ data }: MfaValidationPageState) => data.informations);

  const handleSuccess = useCallback(({ data }: AxiosResponse<MfaChallenge[]>) => {
    dispatch(moveWorkflowForwardByName('workflows.mfa', {
      ...getScopedState((state: MfaValidationPageState) => state.values.informations),
      hasEmailChanged: data?.some(({ type, challengeRequired }: { type: string; challengeRequired: boolean }) => type === 'email' && challengeRequired),
      hasPhoneChanged: data?.some(({ type, challengeRequired }: { type: string; challengeRequired: boolean }) => type === 'sms' && challengeRequired),
    }));
  }, [dispatch, getScopedState]);

  const transformRequest = useCallback((data: MfaInformations): { emailAddress: string; phoneNumber: string } => ({
    emailAddress: data.email,
    phoneNumber: data.phone,
  }), []);

  const transformResponse = useCallback((response: { data: { challenges: MfaChallenge[] } }) => (
    response.data.challenges?.filter((challenge) => challenge.type === 'email' || challenge.type === 'sms')
  ), []);

  useMarkPageLoaded();

  return (
    <CreateForm
      name="forms.informations"
      url={`${getIdentityManagementApiRoot()}/requiredchallenges`}
      valuesSrc="values.mfa.informations"
      valuesDst="values.informations"
      dataSrc="values.informations"
      dataDst="data.informations.challenges"
      defaultValues={informations}
      defaultCol={12}
      cleanValues
      messageBottomBorder={36}
      transformRequest={transformRequest}
      transformResponse={transformResponse}
      onSuccess={handleSuccess}
    >
      <FormPageTitle data-testid="mfaTitle">{t('steps.informations.title')}</FormPageTitle>
      <Block white p-h={0} p-t={0}>
        <InformationsSection
          data-testid="mfaInfo"
          informationsLabel={<SafeHtml>{t('steps.informations.info')}</SafeHtml>}
        />
        <FormSection>
          <FormRow>
            <FormCol
              label={
                <span data-testid="mfaInformationsEmail">
                  {t('steps.informations.emailLabel')}
                </span>
              }
            >
              <EmailControl name="email" required />
            </FormCol>
          </FormRow>
          <FormRow>
            <FormCol
              label={t('steps.informations.phoneLabel')}
              data-testid="mfaInformationsPhone"
            >
              <PhoneControl name="phone" required />
            </FormCol>
          </FormRow>
          <MfaActionBar>
            <PrimarySubmitButton>{t('steps.informations.confirmButtonText')}</PrimarySubmitButton>
          </MfaActionBar>
        </FormSection>
      </Block>
    </CreateForm>
  );
};

export default MfaInformationsStep;
